import { parseMarkdownLocationLink } from '@aline/common';

export interface DocCitation {
  href: string;
  referenceText: string;
}

/**
 * Matches `[text](#doc/<docId>/ver/<verId>/loc/<anchor> "optional title")`-style citation
 * links. Deliberately does NOT match the raw `[docId|locationAnchor]` pipe format some
 * extraction-side reasons still use before a citation is resolved to a full location
 * link — those simply won't match, which is the desired graceful degradation.
 */
const DOC_CITATION_LINK_PATTERN = /\[([^\]]*)\]\((#doc\/\S+?)(?:\s+"([^"]*)")?\)/g;

/**
 * Width in PDF points of a `loc/<page>:<x1>:<y1>:<x2>:<y2>` file-location anchor's rect, or
 * null for anchors in any other shape (Aline-document locations, unresolved pipe citations).
 */
function getFileLocationAnchorWidth(href: string): number | null {
  const locationId = parseMarkdownLocationLink(href)?.locationId;
  if (!locationId) return null;

  const parts = locationId.split(':').map(parseFloat);
  if (parts.length !== 5 || parts.some(Number.isNaN)) return null;
  return Math.abs(parts[3] - parts[1]);
}

/**
 * Extracts the citation of a self-referencing relationship's own titled section out of its
 * reasons markdown (`llmReasonsMarkdown` — the same field the relationship brain-icon
 * tooltip renders). The title is where a click on the relationship should land, but its
 * position among the reason's citations follows the narrative and varies (an exhibit cites
 * the referencing sentence first and the title last; a bound-in SOW cites its title first
 * and its signature blocks last) — so the title is picked by geometry instead: paragraph
 * rects are full-line-width for prose while a heading is a short centered box, making the
 * narrowest rect the title. Ties keep the later citation. Citations without a parsable
 * page:rect anchor lose to any that have one; if none do, the last citation wins as
 * written. Returns null when the text has no citation in the resolved link format, so
 * callers can fall back to their own default behavior rather than guess.
 */
export function getInternalReferenceTitleCitation(
  markdown: string | null | undefined
): DocCitation | null {
  if (!markdown) return null;

  let best: { citation: DocCitation; width: number } | null = null;
  for (const [, linkText, href, quotedTitle] of markdown.matchAll(DOC_CITATION_LINK_PATTERN)) {
    const citation = { href, referenceText: quotedTitle || linkText || '' };
    const width = getFileLocationAnchorWidth(href) ?? Number.POSITIVE_INFINITY;
    if (!best || width <= best.width) best = { citation, width };
  }
  return best?.citation ?? null;
}
